import axios from 'axios';
import { API } from '../config';
import { corpoCoperturaTim } from './coperturaTimCorpo';
import { sessioneScaduta } from './sessioneScaduta';

// Istanza pubblica (senza interceptor di logout dello staff).
const pub = axios.create();

export function adapterToken(token) {
  const P = (p) => `${API}/pubblico/${p}`;
  return {
    variant: 'link',
    loadCaps: () => pub.get(P('capabilities'), { params: { token } }).then(r => r.data),
    loadStats: () => pub.get(P('stats'), { params: { token } }).then(r => r.data),
    loadStatsMensile: () => pub.get(P('stats/mensile'), { params: { token } }).then(r => r.data),
    loadCronologia: () => pub.get(P('cronologia'), { params: { token } }).then(r => r.data?.items || []),
    topo: {
      comuni: (q) => pub.get(P('toponomastica/comuni'), { params: { token, q } }).then(r => r.data.items),
      vie: (q, cp, cc) => pub.get(P('toponomastica/vie'), { params: { token, q, codProv: cp, codCom: cc } }).then(r => r.data.items),
      civici: (cfv) => pub.get(P('toponomastica/civici'), { params: { token, codForteVia: cfv } }).then(r => r.data.items),
    },
    ricercaStart: (numero) => pub.post(P('ricerca'), { token, numero }).then(r => r.data),
    ricercaStato: (id) => pub.get(P(`ricerca/${id}`), { params: { token } }).then(r => r.data),
    coperturaStart: (a) => pub.post(P('copertura'), { token, ...a }).then(r => r.data),
    coperturaStato: (id) => pub.get(P(`copertura/${id}`), { params: { token } }).then(r => r.data),
    // Seconda fonte: il sito TIM. Non usa credenziali SalesMate, quindi risponde anche
    // quando gli account di sistema hanno la password scaduta.
    coperturaTimStart: (a) => pub.post(P('copertura-tim'), { token, ...corpoCoperturaTim(a) }).then(r => r.data),
    coperturaTimStato: (id) => pub.get(P(`copertura-tim/${id}`), { params: { token } }).then(r => r.data),
    dominioStart: (dominio) => pub.post(P('dominio'), { token, dominio }).then(r => r.data),
    dominioStato: (id) => pub.get(P(`dominio/${id}`), { params: { token } }).then(r => r.data),
    indagaStart: (numero) => pub.post(P('indaga'), { token, numero }).then(r => r.data),
    indagaStato: (id) => pub.get(P(`indaga/${id}`), { params: { token } }).then(r => r.data),
    registrazioneUrl: (id) => `${API}/pubblico/registrazione/${encodeURIComponent(id)}?token=${encodeURIComponent(token)}`,
    registrazioneElimina: (id) => pub.delete(P(`registrazione/${encodeURIComponent(id)}`), { params: { token } }).then(r => r.data),
    vicineTrova: (lat, lon, raggio, page) => pub.get(P('vicine'), { params: { token, lat, lon, raggio, page: page || '' } }).then(r => r.data),
    vicineDettaglio: (id) => pub.get(P(`vicine/${encodeURIComponent(id)}`), { params: { token } }).then(r => r.data),
    vicineImporta: (aziende) => pub.post(P('vicine/importa'), { token, aziende }).then(r => r.data),
    vicineImportaStato: () => pub.get(P('vicine/importa/stato'), { params: { token } }).then(r => r.data),
  };
}

export function adapterAgente(getToken, onLogout) {
  const inst = axios.create();
  inst.interceptors.request.use((cfg) => {
    const t = getToken();
    if (t) cfg.headers.Authorization = `Bearer ${t}`;
    return cfg;
  });
  // Sessione scaduta (o token di un'altra origine): si torna al login. Senza questo,
  // l'app restava su «Non riesco a contattare il servizio» mentre il servizio rispondeva,
  // e dal login non si passava piu' (07/10/2026).
  inst.interceptors.response.use((r) => r, (e) => {
    if (sessioneScaduta(e) && typeof onLogout === 'function') onLogout();
    return Promise.reject(e);
  });
  const A = (p) => `${API}/agente/${p}`;
  return {
    variant: 'agente', onLogout,
    loadCaps: () => inst.get(A('capabilities')).then(r => r.data),
    loadStats: () => inst.get(A('stats')).then(r => r.data),
    loadCronologia: () => inst.get(A('cronologia')).then(r => r.data?.items || []),
    topo: {
      comuni: (q) => inst.get(A('toponomastica/comuni'), { params: { q } }).then(r => r.data.items),
      vie: (q, cp, cc) => inst.get(A('toponomastica/vie'), { params: { q, codProv: cp, codCom: cc } }).then(r => r.data.items),
      civici: (cfv) => inst.get(A('toponomastica/civici'), { params: { codForteVia: cfv } }).then(r => r.data.items),
      // Icona "localizzami" nella Copertura: reverse-geocoding gratis via Nominatim/OSM.
      // Richiede secure context (https): fuori da https AddressCascade avvisa e non chiama il GPS.
      reverseGeo: (lat, lon) => inst.get(A('reverse-geo'), { params: { lat, lon } }).then(r => r.data),
    },
    ricercaStart: (numero) => inst.post(A('ricerca'), { numero }).then(r => r.data),
    ricercaStato: (id) => inst.get(A(`ricerca/${id}`)).then(r => r.data),
    coperturaStart: (a) => inst.post(A('copertura'), a).then(r => r.data),
    coperturaStato: (id) => inst.get(A(`copertura/${id}`)).then(r => r.data),
    // Copertura sito TIM (alternativa a SalesMate): job server-side, prosegue a PWA chiusa e notifica a fine.
    coperturaTimStart: (a) => inst.post(A('copertura-tim'), corpoCoperturaTim(a)).then(r => r.data),
    coperturaTimStato: (id) => inst.get(A(`copertura-tim/${id}`)).then(r => r.data),
    coperturaTimAnnulla: (id) => inst.delete(A(`copertura-tim/${id}`)).then(r => r.data),
    lucchettoStart: (codice_fiscale) => inst.post(A('lucchetto'), { codice_fiscale }).then(r => r.data),
    lucchettoStato: (id) => inst.get(A(`lucchetto/${id}`)).then(r => r.data),
    lucchettoScreenshotUrl: (cf) => `${API}/agente/lucchetto/screenshot/${encodeURIComponent(cf)}?token=${encodeURIComponent(getToken() || '')}`,
    lucchettoConsistenze: (cf) => inst.get(A(`lucchetto/consistenze/${encodeURIComponent(cf)}`)).then(r => r.data),
    registrazioneUrl: (id) => `${API}/agente/registrazione/${encodeURIComponent(id)}?token=${encodeURIComponent(getToken() || '')}`,
    aggiornaSsmate: (password) => inst.post(A('ssmate-credenziali'), { password }).then(r => r.data),
    dominioStart: (dominio, indirizzo) => inst.post(A('dominio'), { dominio, indirizzo }).then(r => r.data),
    dominioStato: (id) => inst.get(A(`dominio/${id}`)).then(r => r.data),
    vicineTrova: (lat, lon, raggio, page) => inst.get(A('vicine'), { params: { lat, lon, raggio, page: page || '' } }).then(r => r.data),
    vicineDettaglio: (id) => inst.get(A(`vicine/${encodeURIComponent(id)}`)).then(r => r.data),
    loadNotifiche: () => inst.get(A('notifiche')).then(r => r.data),
    notificaLetta: (id) => inst.post(A(`notifiche/${encodeURIComponent(id)}/letta`)).then(r => r.data),
    notificheLetteTutte: () => inst.post(A('notifiche/lette-tutte')).then(r => r.data),
  };
}
